// src/services/StorageService.js
import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  getMetadata,
  deleteObject,
  updateMetadata,
} from "firebase/storage";

import { storage } from "../firebase";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuidv4 } from "uuid";

/**
 * uploadFile
 * Uploads a file to Firebase Storage under a path that matches our Python implementation.
 * Path structure: users/{userId}/folders/{folderId}/{category}/{fileName}
 */
export async function uploadFile(userId, folderId, subfolderId, file) {
  try {
    // First, get the subfolder name
    let subfolderName = "";
    try {
      const subfolderRef = doc(
        db,
        "users",
        userId,
        "folders",
        folderId,
        "subfolders",
        subfolderId
      );
      const subfolderSnap = await getDoc(subfolderRef);

      if (subfolderSnap.exists()) {
        subfolderName = subfolderSnap.data().subfolderName;
      } else {
        // Fallback in case subfolder not found
        subfolderName = `subfolder-${subfolderId}`;
      }
    } catch (error) {
      console.error("Error getting subfolder name:", error);
      // Fallback to using subfolder ID
      subfolderName = `subfolder-${subfolderId}`;
    }

    // Set the storage path to match Python implementation
    const storagePath = `users/${userId}/folders/${folderId}/${subfolderName}/${file.name}`;
    console.log(`Uploading file to path: ${storagePath}`);

    // Upload the file
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(fileRef);
    console.log("Download URL obtained:", downloadURL);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

/**
 * uploadTempFile
 * Uploads a file to Firebase Storage under a temporary path for processing.
 */
export async function uploadTempFile(userId, file, pageCount = null) {
  try {
    const uniqueId = uuidv4();

    // Determine the subfolder for the file type
    let subfolder = "others";
    if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
      subfolder = "pdfs";
    } else if (file.type.startsWith("image/")) {
      subfolder = "images";
    }

    // Organize by subfolder under temporary_files
    const storagePath = `users/${userId}/temporary_files/${subfolder}/${file.name}`;
    console.log(`Uploading temp file to path: ${storagePath}`);

    const fileRef = ref(storage, storagePath);
    // Upload file with initial metadata including pageCount if provided
    const metadata = {
      customMetadata: {
        uuid: uniqueId,
      },
    };
    if (pageCount !== null) {
      metadata.customMetadata.page = pageCount.toString();
    }
    await uploadBytes(fileRef, file, metadata);

    // Get download URL
    const downloadURL = await getDownloadURL(fileRef);
    console.log("Temp file download URL obtained:", downloadURL);

    // Update metadata to include downloadURL and pageCount if provided
    const updatedMetadata = {
      customMetadata: {
        uuid: uniqueId,
        downloadURL: downloadURL,
        isPredicted: false,
      },
    };
    await updateMetadata(fileRef, updatedMetadata);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading temp file:", error);
    throw error;
  }
}

/**
 * deleteFile
 * Deletes a file from Firebase Storage
 */
export async function deleteFile(storagePath) {
  try {
    const fileRef = ref(storage, storagePath);
    await deleteObject(fileRef);
    return true;
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
}

/**
 * updateIsPredictedMetadata
 * Updates the isPredicted metadata field for a file in Firebase Storage
 * @param {string} storagePath - The full path of the file in storage
 * @param {boolean} isPredicted - The value to set for isPredicted (true or false)
 */
export async function updateIsPredictedMetadata(storagePath, isPredicted) {
  try {
    const fileRef = ref(storage, storagePath);
    const metadata = await getMetadata(fileRef);
    const customMetadata = metadata.customMetadata || {};
    customMetadata.isPredicted = isPredicted.toString();

    await updateMetadata(fileRef, { customMetadata });
    console.log("isPredicted is updated to true [SUCCESS]");
  } catch (error) {
    console.error("Error updating isPredicted metadata:", error);
    throw error;
  }
}

/**
 * getStoragePath
 * Utility function to generate a consistent storage path
 */
export function getStoragePath(userId, folderId, subfolderName, fileName) {
  return `users/${userId}/folders/${folderId}/${subfolderName}/${fileName}`;
}

/**
 * listFilesInStoragePath
 * Lists all files under a given storage path and returns an array of file metadata with download URLs
 * @param {string} storagePath - The path in Firebase Storage to list files from
 * @returns {Promise<Array>} - Array of file objects with id, name, size, type, and downloadURL
 */
export async function listFilesInStoragePath(storagePath) {
  try {
    const folderRef = ref(storage, storagePath);
    const listResult = await listAll(folderRef);
    const files = await Promise.all(
      listResult.items.map(async (itemRef) => {
        const metadata = await getMetadata(itemRef);
        const customMeta = metadata.customMetadata || {};
        // Filter out files where isPredicted is "true"
        if (customMeta.isPredicted === "true") {
          return null;
        }
        return {
          id: itemRef.name,
          name: itemRef.name,
          size: metadata.size,
          type: metadata.contentType,
          downloadURL: customMeta.downloadURL,
          page: customMeta.page || null,
          storagePath: itemRef.fullPath,
        };
      })
    );
    // Filter out nulls from files array
    return files.filter((file) => file !== null);
  } catch (error) {
    console.error("Error listing files in storage path:", error);
    return [];
  }
}

/**
 * listUserTemporaryImages
 * Lists all image files under users/{userId}/temporary_files/images
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of image file objects
 */
export async function listUserTemporaryImages(userId) {
  const path = `users/${userId}/temporary_files/images`;
  return await listFilesInStoragePath(path);
}

/**
 * listUserTemporaryPdfs
 * Lists all PDF files under users/{userId}/temporary_files/pdfs
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of PDF file objects
 */
export async function listUserTemporaryPdfs(userId) {
  const path = `users/${userId}/temporary_files/pdfs`;
  return await listFilesInStoragePath(path);
}
